import React, { useMemo } from 'react';
import { ChevronsDownUp, ChevronsUpDown } from 'lucide-react';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarRail,
  SidebarTrigger,
  useSidebar,
} from '@shared/ui/sidebar';
import { Button } from '@shared/ui/button';
import { SidebarSuiteHeader } from './SidebarSuiteHeader';
import { SidebarNavGroup } from './SidebarNavGroup';
import { useActiveSuite } from '../useActiveSuite';
import { useGroupCollapse } from './useGroupCollapse';
import { SuiteNavItem } from '@suites/manifest';
import { useAuth } from '@shared/auth/AuthContext';

function groupNavItems(nav?: SuiteNavItem[]) {
  if (!nav) return [];
  const groups: { name?: string; items: SuiteNavItem[] }[] = [];
  const map = new Map<string, SuiteNavItem[]>();
  const ungrouped: SuiteNavItem[] = [];

  for (const item of nav) {
    if (item.group) {
      if (!map.has(item.group)) {
        map.set(item.group, []);
      }
      map.get(item.group)!.push(item);
    } else {
      ungrouped.push(item);
    }
  }

  if (ungrouped.length > 0) groups.push({ items: ungrouped });
  map.forEach((items, name) => groups.push({ name, items }));
  return groups;
}

export const AppSidebar: React.FC = () => {
  const activeSuite = useActiveSuite();
  const { role, hasModule, hasPermission } = useAuth();
  const { state } = useSidebar();
  const isCollapsed = state === 'collapsed';

  const filteredNav = useMemo(() => {
    if (!activeSuite?.nav) return [];
    
    return activeSuite.nav.filter((item) => {
      // 1. Module entitlement
      if (item.moduleId && !hasModule(activeSuite.id, item.moduleId)) return false;

      // 2. Specific item role restriction
      if (item.allowedRoles && item.allowedRoles.length > 0 && !item.allowedRoles.includes(role)) {
        return false;
      }

      // 3. Permission capability check
      if (item.permission && !hasPermission(item.permission)) {
        return false;
      }

      // 4. Role-specific view filters
      if (role === 'owner' || role === 'admin' || role === 'branch_head') {
        return true;
      }

      if (role === 'student' || role === 'parent') {
        if (activeSuite.id === 'core') {
          return item.path !== '/core/directory';
        }
        if (activeSuite.id === 'lms') {
          const allowedPaths = [
            '/lms/live',
            '/lms/learn',
            '/lms/schedule',
            '/lms/materials',
            '/lms/exam-portal',
            '/lms/exam-results',
            '/lms/quiz',
            '/lms/questions',
            '/lms/candidate-verify',
          ];
          return allowedPaths.includes(item.path);
        }
        if (activeSuite.id === 'sis') {
          const allowedPaths = [
            '/sis/dashboard',
            '/sis/hire-tutor',
            '/sis/my-tutors',
            '/sis/topics',
            '/sis/profile',
            '/sis/rewards',
          ];
          return allowedPaths.includes(item.path);
        }
        if (activeSuite.id === 'fees') {
          return item.path === '/fees/invoices';
        }
        if (activeSuite.id === 'communication') {
          return ['/communication/chat', '/communication/forum'].includes(item.path);
        }
      }

      if (role === 'teacher') {
        if (activeSuite.id === 'core') {
          return item.path !== '/core/fee-book';
        }
        if (activeSuite.id === 'lms') {
          const allowedPaths = [
            '/lms/live',
            '/lms/schedule',
            '/lms/courses',
            '/lms/materials',
            '/lms/questions',
            '/lms/assessments',
            '/lms/pdf-exams',
            '/lms/mock-generator',
            '/lms/proctoring',
            '/lms/violations',
            '/lms/security-policies',
            '/lms/candidate-verify',
            '/lms/evaluate',
            '/lms/reports',
          ];
          return allowedPaths.includes(item.path);
        }
        if (activeSuite.id === 'hr') {
          const allowedPaths = [
            '/hr/dashboard',
            '/hr/live-sessions',
            '/hr/marketplace',
            '/hr/reviews',
            '/hr/teachers',
            '/hr/teaching-load',
            '/hr/leaves',
          ];
          return allowedPaths.includes(item.path);
        }
        if (activeSuite.id === 'sis') {
          const allowedPaths = [
            '/sis/my-tutors',
            '/sis/students',
            '/sis/enrollments',
            '/sis/attendance',
          ];
          return allowedPaths.includes(item.path);
        }
        if (activeSuite.id === 'scheduling') {
          return [
            '/scheduling/timetable',
            '/scheduling/batches',
            '/scheduling/calendar',
            '/scheduling/master-classes',
            '/scheduling/assignments',
          ].includes(item.path);
        }
        if (activeSuite.id === 'communication') {
          return ['/communication/chat', '/communication/broadcasts', '/communication/forum'].includes(item.path);
        }
      }

      if (role === 'counselor') {
        if (activeSuite.id === 'crm') return true;
        if (activeSuite.id === 'sis') return ['/sis/students', '/sis/enrollments'].includes(item.path);
      }

      if (role === 'accountant') {
        if (activeSuite.id === 'fees') return true;
        if (activeSuite.id === 'hr') return ['/hr/dashboard', '/hr/payroll'].includes(item.path);
      }

      return true;
    });
  }, [activeSuite, role, hasModule, hasPermission]);

  const groupedNav = useMemo(() => groupNavItems(filteredNav), [filteredNav]);

  const groupNames = useMemo(() => {
    return groupedNav
      .map((g) => g.name)
      .filter((name): name is string => typeof name === 'string' && name.length > 0);
  }, [groupedNav]);

  const { isGroupOpen, toggleGroup, toggleAll, hasAnyOpen } = useGroupCollapse(
    activeSuite?.id,
    groupNames
  );

  if (!activeSuite) return null;

  return (
    <Sidebar
      collapsible="icon"
      className="top-[var(--shell-header-h)] h-[calc(100svh-var(--shell-header-h))] border-r border-sidebar-border bg-sidebar z-20"
    >
      <SidebarSuiteHeader />
      <SidebarContent className="py-2">
        {groupedNav.map((group, idx) => {
          const gName = group.name;
          return (
            <SidebarNavGroup
              key={gName || `group_${idx}`}
              groupName={gName}
              items={group.items}
              isOpen={gName ? isGroupOpen(gName) : true}
              onToggle={gName ? () => toggleGroup(gName) : undefined}
            />
          );
        })}
      </SidebarContent>
      <SidebarFooter className="border-t border-sidebar-border p-2">
        <div className="flex items-center justify-between w-full">
          <SidebarTrigger className="h-7 w-7 text-muted-foreground hover:text-foreground" />
          {!isCollapsed && groupNames.length > 0 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={toggleAll}
              className="h-7 px-2 text-[11px] font-medium text-muted-foreground hover:text-foreground flex items-center gap-1.5 transition-colors cursor-pointer"
              title={hasAnyOpen ? 'Collapse all subcategories' : 'Open all subcategories'}
            >
              {hasAnyOpen ? (
                <>
                  <ChevronsDownUp className="h-3.5 w-3.5" />
                  <span>Collapse All</span>
                </>
              ) : (
                <>
                  <ChevronsUpDown className="h-3.5 w-3.5" />
                  <span>Open All</span>
                </>
              )}
            </Button>
          )}
        </div>
      </SidebarFooter>
      <SidebarRail />
    </Sidebar>
  );
};
